import { DOCUMENT } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    OnDestroy,
    effect,
    inject,
    input,
    model,
    output,
    viewChild,
} from '@angular/core';

import { BackgroundVariant } from '../../_shared/models/hero/hero-variant.enum';
import { IconComponent } from '../../icon/icon.component';

@Component({
    selector: 'sirius-modal2',
    templateUrl: './modal2.component.html',
    styleUrl: './modal2.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [IconComponent],
    host: {
        '[class.is-fullscreen]': 'fullscreen()',
        '[class.is-dark]': "variant() === 'dark'",
    },
})
export class Modal2Component implements OnDestroy {
    readonly open = model(false);
    readonly fullscreen = input(false);
    readonly titleId = input<string | undefined>(undefined);
    readonly descriptionId = input<string | undefined>(undefined);
    readonly closeAriaLabel = input('Close dialog');

    /** Background of the dialog surface, which also determines its foreground colours. */
    readonly variant = input<BackgroundVariant>(BackgroundVariant.LIGHT);

    readonly modalOpen = output<void>();
    readonly modalClose = output<void>();

    private readonly _dialogRef = viewChild<ElementRef<HTMLDialogElement>>('dialog');
    private readonly _closeButtonRef = viewChild<ElementRef<HTMLButtonElement>>('closeButton');

    private readonly _document = inject(DOCUMENT);

    private _lastFocusedElement?: HTMLElement | null;

    constructor() {
        effect(() => {
            const dialog = this._dialogRef()?.nativeElement;
            if (!dialog) return;

            const shouldOpen = this.open();
            if (shouldOpen && !dialog.open) {
                this.showDialog(dialog);
            } else if (!shouldOpen && dialog.open) {
                this.hideDialog(dialog);
            }
        });
    }

    ngOnDestroy(): void {
        // Consumers close by destroying the component (an `@if` around an open modal), which
        // happens in the same change-detection turn, so the open->closed effect never runs.
        const dialog = this._dialogRef()?.nativeElement;
        if (dialog?.open) {
            this.hideDialog(dialog);
        }
    }

    onDialogCancel(event: Event): void {
        event.preventDefault();
        this.closeDialog();
    }

    closeDialog(): void {
        this.open.set(false);
        this.modalClose.emit();
    }

    private showDialog(dialog: HTMLDialogElement): void {
        this._lastFocusedElement = this._document.activeElement as HTMLElement | null;
        dialog.showModal();
        this._closeButtonRef()?.nativeElement.focus();
        this.modalOpen.emit();
    }

    private hideDialog(dialog: HTMLDialogElement): void {
        const previouslyFocused = this._lastFocusedElement;
        this._lastFocusedElement = null;

        dialog.close();

        // Browsers restore focus to the invoker on close; refocusing from script would
        // re-resolve :focus-visible and paint a ring after a pointer-driven close.
        const active = this._document.activeElement;
        if (!active || active === this._document.body) {
            previouslyFocused?.focus();
        }
    }
}
